// Les pièces d'avatar ajoutées sans redéploiement. Une pièce est un bloc : son dessin
// (du SVG dans le repère 120 × 120 de l'avatar) et ses règles d'obtention. Le bloc se
// colle dans /admin/avatar-items, il est gardé en base (table avatar_pieces) et le site
// le charge partout où un avatar se dessine. Les pièces de base restent dans catalog.js.
//
// Un bloc :
// {
//   "item": "headwear:witch",              catégorie:identifiant (voir CUSTOM_CATEGORIES)
//   "name": { "fr": "Chapeau de sorcière", "en": "Witch hat" },
//   "svg": "<path d=\"…\" fill=\"#2E2A3C\" />…",
//   "svgSnug": "…",                        (couvre-chef) dessin pour un crâne sans volume de cheveux
//   "hat": { "line": 37 },                 (couvre-chef) les cheveux ne dépassent que sous cette hauteur
//   "hat": { "line": 44, "wide": 1.13 },   (couvre-chef) en plus : sa taille sur une coiffure volumineuse
//   "neckScale": false,                    (cou) false : la pièce ne suit pas la largeur du cou
//   "svgBack": "…",                        (coiffure) ce qui passe derrière la tête : longueurs, volume
//   "svgUnderHat": "…",                    (coiffure) dessin plus ras, pris sous un couvre-chef posé sur le crâne
//   "svgBackUnderHat": "…",                (coiffure) l'arrière qui va avec
//   "coversEars": true,                    (coiffure) les cheveux tombent sur les oreilles
//   "snug": true,                          (coiffure) pas de volume : les couvre-chefs se posent au ras du crâne
//   "wide": true,                          (coiffure) elle déborde du crâne : les couvre-chefs prennent une taille de plus
//   "base": "top",                         (tenue) la couleur du buste : « top » (celle choisie par le membre) ou un code
//   "neckline": "round" | "vee" | "none",  (tenue) l'encolure qui laisse voir la peau
//   "svgCollar": "…",                      (tenue) ce qui suit la largeur du cou : col, capuche
//   "svgOpen": "…",                        (bouche) la même bouche grande ouverte, pour le rire et la surprise
//   "svgSad": "…",                         (bouche) la même bouche triste
//   "visibility": "always" | "event" | "hidden",
//   "condition": { … }                     absente : pièce libre, offerte à tout le monde
// }
//
// `condition` :
// - { "type": "count", "metric", "target", "periods" } : avoir terminé `target` sessions
//   ("sessions"), quiz ("quizzes"), quêtes du jour ("quests") ou formations ("courses").
//   `periods` (facultatif) : la condition doit être remplie à l'intérieur d'une des périodes
//   [{ "from", "to" }], à l'heure de Paris. Le compte se refait sur les dates : un membre
//   qui l'a remplie débloque la pièce même s'il ouvre l'éditeur après la fin de la période.
// - { "type": "claim", "periods" } : offerte, à récupérer d'un clic (pendant une période s'il y en a).
// - { "type": "badge", "badgeId", "badgeName": { "fr", "en" } } : posséder ce badge.
// - { "type": "gift" } : donnée par l'équipe, jamais débloquée d'office.
// - { "type": "event", "event": "halloween-2026" } : gagnée pendant un événement de puzzles
//   (lib/avatar/events.js), en finissant son puzzle ou par un palier de l'objectif commun.
//
// `visibility` : ce que voit le membre qui n'a pas encore la pièce.
// - "always" (par défaut) : toujours dans l'éditeur, avec son cadenas et sa condition ;
// - "event" : seulement pendant sa période, sinon réservée à ceux qui l'ont ;
// - "hidden" : jamais avant d'être débloquée.
//
// Selon la catégorie :
// - coiffure (hair) : `svg` est ce qui passe devant le visage (frange, mèches, pattes), `svgBack`
//   ce qui passe derrière. À dessiner avec les mots hair et hair-shade, pour suivre la couleur
//   de cheveux du membre. Un couvre-chef qui cache les cheveux les coupe de lui-même. Une
//   coiffure haute donne en plus `svgUnderHat` : sous un couvre-chef posé sur le crâne ou serré
//   autour de la tête (béret, haut-de-forme, mortier, couronne, chapeau de fête, bandeau,
//   bandana), c'est ce dessin plus ras qui est pris. Le béret, le bandana, le mortier et le
//   haut-de-forme cachent en plus ce qui dépasse au-dessus d'eux.
// - couvre-chef (headwear) : à dessiner pour une coiffure courte (le crâne va de x = 32 à 88).
//   Sur des cheveux longs ou épais, qui vont de 26 à 94, un couvre-chef serré sur les tempes
//   (bonnet, bandeau) paraît trop petit : `hat.wide` l'agrandit d'un bloc autour du point
//   (60, line), 1.13 pour un bonnet. Inutile pour un chapeau à large bord.
// - tenue (top) : le buste est dessiné d'office dans la couleur `base`, à la carrure du membre
//   (fine, normale, musclée). `svg` se pose dessus et ne dépasse jamais du buste, quelle que
//   soit la carrure : motifs, boutons, bretelles. `svgCollar` suit la largeur du cou.
//
// - bouche (mouth) : `svg` remplace toute la bouche (centre vers x = 60, y = 73). `svgOpen` est
//   la même bouche ouverte : elle est prise quand le personnage rit ou s'étonne. `svgSad` est
//   la même bouche triste. Sans elles, il rit ou s'attriste avec la bouche de base, et la pièce
//   revient ensuite.
//
// Couleurs du dessin : un code #RRGGBB, ou un mot qui suit l'avatar du membre : accent
// et accent-shade (la couleur des accessoires), hair et hair-shade, skin et skin-shade,
// top et top-shade (la couleur du haut).

import { AVATAR_CATEGORIES, CUSTOM_CATEGORIES } from "./catalog.js";

export { CUSTOM_CATEGORIES };
export const METRICS = ["sessions", "quizzes", "quests", "courses"];
export const VISIBILITIES = ["always", "event", "hidden"];

const ITEM = /^([a-zA-Z]+):([a-z0-9-]{1,24})$/;
const MAX_SVG = 20000;

// --- Le dessin ---------------------------------------------------------------------
// Le SVG collé est redessiné balise par balise : seules des formes simples et des
// attributs de dessin passent. Tout le reste (script, lien, style, image, texte, url())
// fait refuser le bloc. C'est ce texte reconstruit, jamais le texte collé, qui est affiché.

const TAGS = ["g", "path", "circle", "ellipse", "rect", "line", "polygon", "polyline"];
const ATTRIBUTES = [
    "d", "fill", "stroke", "stroke-width", "stroke-linecap", "stroke-linejoin", "stroke-dasharray", "opacity", "fill-opacity",
    "stroke-opacity", "fill-rule", "clip-rule", "cx", "cy", "r", "rx", "ry", "x", "y", "x1", "y1", "x2", "y2", "width", "height", "points", "transform",
];
const COLOR_WORDS = ["none", "accent", "accent-shade", "hair", "hair-shade", "skin", "skin-shade", "top", "top-shade"];
export const NECKLINES = ["round", "vee", "none"];
const COLOR = /^#[0-9a-fA-F]{3,8}$/;
const PLAIN = /^[a-zA-Z0-9 .,()\-+]*$/;
const kebab = (name) => name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

// Rend { svg } (le dessin reconstruit) ou { error }.
export function sanitizeSvg(markup) {
    if (typeof markup !== "string" || !markup.trim()) return { error: "le dessin est vide" };
    if (markup.length > MAX_SVG) return { error: `le dessin dépasse ${MAX_SVG} caractères` };
    const token = /<(\/?)([a-zA-Z]+)((?:\s+[a-zA-Z-]+\s*=\s*"[^"]*")*)\s*(\/?)>/y;
    const attribute = /([a-zA-Z-]+)\s*=\s*"([^"]*)"/g;
    const open = [];
    let out = "";
    let index = 0;
    while (index < markup.length) {
        const space = /\s*/y;
        space.lastIndex = index;
        index += space.exec(markup)[0].length;
        if (index >= markup.length) break;
        token.lastIndex = index;
        const match = token.exec(markup);
        if (!match) return { error: `dessin illisible vers « ${markup.slice(index, index + 30)} » (seules des balises de forme sont acceptées, sans texte autour)` };
        index = token.lastIndex;
        const [, closing, rawTag, rawAttributes, selfClosing] = match;
        const tag = rawTag.toLowerCase();
        if (!TAGS.includes(tag)) return { error: `balise <${rawTag}> refusée (acceptées : ${TAGS.join(", ")})` };
        if (closing) {
            if (open.pop() !== tag) return { error: `balise </${tag}> mal fermée` };
            out += `</${tag}>`;
            continue;
        }
        let attributes = "";
        for (const [, rawName, value] of rawAttributes.matchAll(attribute)) {
            const name = kebab(rawName);
            if (!ATTRIBUTES.includes(name)) return { error: `attribut « ${rawName} » refusé` };
            const colour = name === "fill" || name === "stroke";
            const valid = colour ? COLOR.test(value) || COLOR_WORDS.includes(value) : PLAIN.test(value) && !/url/i.test(value);
            if (!valid) return { error: `valeur « ${value.slice(0, 40)} » refusée pour ${rawName}` };
            attributes += ` ${name}="${value}"`;
        }
        if (selfClosing) out += `<${tag}${attributes}/>`;
        else {
            open.push(tag);
            out += `<${tag}${attributes}>`;
        }
    }
    if (open.length) return { error: `balise <${open.pop()}> jamais fermée` };
    return { svg: out };
}

// Le dessin prêt à afficher : les mots de couleur prennent les teintes de l'avatar.
// `field` : le dessin voulu (svg, svgBack, svgCollar) ; `snug` prend svgSnug s'il existe.
// Le résultat est gardé par pièce et par jeu de teintes : un avatar se redessine souvent
// (survol, réaction), et rendre le même objet évite à React de réécrire le dessin.
const drawn = new WeakMap();
export function pieceMarkup(piece, { snug = false, accent, hair, skin, top } = {}, field = "svg") {
    const source = (field === "svg" && snug && piece.svgSnug) || piece[field];
    if (!source) return null;
    const key = `${field}|${source === piece.svgSnug ? 1 : 0}|${accent?.fill}|${hair?.fill}|${skin?.fill}|${top?.fill}`;
    let cache = drawn.get(piece);
    if (!cache) drawn.set(piece, (cache = new Map()));
    if (!cache.has(key)) {
        const colors = {
            accent: accent?.fill, "accent-shade": accent?.shade, hair: hair?.fill, "hair-shade": hair?.shade,
            skin: skin?.fill, "skin-shade": skin?.shade, top: top?.fill, "top-shade": top?.shade,
        };
        cache.set(key, { __html: source.replace(/ (fill|stroke)="([a-z-]+)"/g, (whole, name, word) => (colors[word] ? ` ${name}="${colors[word]}"` : whole)) });
    }
    return cache.get(key);
}

// --- Les règles --------------------------------------------------------------------

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MOMENT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
// « 2026-10-24 » vaut pour la journée entière : début à 00:00:00, fin à 23:59:59.
const bound = (value, end) => (DAY.test(value) ? `${value} ${end ? "23:59:59" : "00:00:00"}` : value);

function cleanPeriods(periods, errors) {
    if (periods === undefined || periods === null) return undefined;
    if (!Array.isArray(periods)) {
        errors.push("periods doit être une liste de { from, to }");
        return undefined;
    }
    const clean = periods.map((period) => ({ from: bound(period?.from, false), to: bound(period?.to, true) }));
    for (const period of clean) {
        if (!MOMENT.test(period.from) || !MOMENT.test(period.to)) errors.push("une période s'écrit { from: \"2026-10-24\", to: \"2026-11-02\" }");
        else if (period.from >= period.to) errors.push(`la période du ${period.from} finit avant de commencer`);
    }
    // Rangées par date : la période à montrer se choisit dans l'ordre.
    return clean.length ? clean.sort((a, b) => (a.from < b.from ? -1 : 1)) : undefined;
}

function cleanCondition(condition, errors) {
    if (condition === undefined || condition === null) return undefined;
    if (typeof condition !== "object") {
        errors.push("condition doit être un objet");
        return undefined;
    }
    if (condition.type === "count") {
        if (!METRICS.includes(condition.metric)) errors.push(`metric doit valoir ${METRICS.join(", ")}`);
        if (!Number.isInteger(condition.target) || condition.target < 1 || condition.target > 10000) errors.push("target doit être un nombre entier, 1 au moins");
        return { type: "count", metric: condition.metric, target: condition.target, periods: cleanPeriods(condition.periods, errors) };
    }
    if (condition.type === "claim") return { type: "claim", periods: cleanPeriods(condition.periods, errors) };
    if (condition.type === "badge") {
        if (!Number.isInteger(condition.badgeId)) errors.push("badgeId doit être l'identifiant d'un badge");
        return { type: "badge", badgeId: condition.badgeId, badgeName: { fr: String(condition.badgeName?.fr || ""), en: String(condition.badgeName?.en || condition.badgeName?.fr || "") } };
    }
    if (condition.type === "gift") return { type: "gift" };
    if (condition.type === "event") {
        if (typeof condition.event !== "string" || !/^[a-z0-9-]{1,60}$/.test(condition.event)) errors.push("condition.event est le slug de l'événement (halloween-2026)");
        return { type: "event", event: condition.event };
    }
    errors.push("condition.type doit valoir count, claim, badge, gift ou event");
    return undefined;
}

// Vérifie un bloc. Rend { piece } (le bloc nettoyé, prêt à enregistrer) ou { errors }.
export function validatePiece(block) {
    const errors = [];
    if (!block || typeof block !== "object" || Array.isArray(block)) return { errors: ["le bloc doit être un objet { item, name, svg… }"] };
    const match = ITEM.exec(typeof block.item === "string" ? block.item : "");
    const key = match?.[1];
    if (!match) errors.push("item s'écrit « catégorie:identifiant », l'identifiant en minuscules, chiffres et tirets (headwear:witch)");
    else if (!CUSTOM_CATEGORIES.includes(key)) errors.push(`catégorie « ${key} » refusée (acceptées : ${CUSTOM_CATEGORIES.join(", ")})`);
    else if (AVATAR_CATEGORIES.find((category) => category.key === key).options.includes(match[2])) errors.push(`« ${match[2]} » est déjà une pièce de base du catalogue`);

    const name = { fr: String(block.name?.fr || "").trim().slice(0, 60), en: String(block.name?.en || "").trim().slice(0, 60) };
    if (!name.fr || !name.en) errors.push("name doit donner le nom en français et en anglais : { fr, en }");

    const piece = { item: block.item, name };
    // Un dessin facultatif ou obligatoire : nettoyé, puis rangé dans la pièce.
    const drawing = (field, required) => {
        const given = block[field] !== undefined && block[field] !== null && block[field] !== "";
        if (!given && !required) return;
        const result = sanitizeSvg(block[field]);
        if (result.error) errors.push(`${field} : ${result.error}`);
        piece[field] = result.svg;
    };
    const only = (field, category, label) => {
        if (block[field] === undefined || block[field] === null) return false;
        if (key !== category) errors.push(`${field} ne vaut que pour ${label}`);
        return key === category;
    };
    // La tenue peut se passer de dessin : un buste d'une couleur est déjà une tenue.
    drawing("svg", key !== "top");
    if (only("svgSnug", "headwear", "un couvre-chef (headwear)")) drawing("svgSnug", false);
    if (only("hat", "headwear", "un couvre-chef (headwear)")) {
        const line = Number(block.hat?.line);
        const wide = block.hat?.wide === undefined || block.hat?.wide === null ? null : Number(block.hat.wide);
        if (!Number.isFinite(line) || line < 25 || line > 55) errors.push("hat.line est une hauteur entre 25 et 55 (44 pour un bonnet, 37 pour un chapeau posé haut)");
        else if (wide !== null && !(wide >= 1 && wide <= 1.25)) errors.push("hat.wide est une taille entre 1 et 1.25 (1.13 pour un bonnet sur des cheveux longs)");
        else piece.hat = wide === null || wide === 1 ? { line } : { line, wide };
    }
    if (only("neckScale", "neckwear", "une pièce de cou (neckwear)") && block.neckScale === false) piece.neckScale = false;
    if (only("svgBack", "hair", "une coiffure (hair)")) drawing("svgBack", false);
    if (only("svgUnderHat", "hair", "une coiffure (hair)")) drawing("svgUnderHat", false);
    if (only("svgBackUnderHat", "hair", "une coiffure (hair)")) drawing("svgBackUnderHat", false);
    if (only("coversEars", "hair", "une coiffure (hair)") && block.coversEars === true) piece.coversEars = true;
    if (only("snug", "hair", "une coiffure (hair)") && block.snug === true) piece.snug = true;
    if (only("wide", "hair", "une coiffure (hair)") && block.wide === true) piece.wide = true;
    if (only("svgCollar", "top", "une tenue (top)")) drawing("svgCollar", false);
    if (only("svgOpen", "mouth", "une bouche (mouth)")) drawing("svgOpen", false);
    if (only("svgSad", "mouth", "une bouche (mouth)")) drawing("svgSad", false);
    if (only("base", "top", "une tenue (top)")) {
        if (COLOR.test(block.base) || ["top", "skin"].includes(block.base)) piece.base = block.base;
        else errors.push("base est une couleur #RRGGBB, « top » (la couleur choisie par le membre) ou « skin »");
    }
    if (only("neckline", "top", "une tenue (top)")) {
        if (NECKLINES.includes(block.neckline)) piece.neckline = block.neckline;
        else errors.push(`neckline doit valoir ${NECKLINES.join(", ")}`);
    }

    piece.visibility = block.visibility === undefined ? "always" : block.visibility;
    if (!VISIBILITIES.includes(piece.visibility)) errors.push(`visibility doit valoir ${VISIBILITIES.join(", ")}`);
    const condition = cleanCondition(block.condition, errors);
    if (condition) piece.condition = condition;
    // « event » : visible pendant sa période, ou pendant l'événement de puzzles qui la fait gagner.
    if (piece.visibility === "event" && !condition?.periods?.length && condition?.type !== "event") errors.push("visibility « event » demande une condition avec au moins une période, ou une condition de type event");

    return errors.length ? { errors } : { piece: JSON.parse(JSON.stringify(piece)) };
}

// Ce que le site lit depuis la base : les blocs valides, rangés par « catégorie:identifiant ».
// Un bloc devenu invalide (catalogue changé, base modifiée à la main) est écarté sans bruit.
export function indexPieces(blocks) {
    const index = {};
    for (const block of Array.isArray(blocks) ? blocks : []) {
        const { piece } = validatePiece(block);
        if (piece) index[piece.item] = piece;
    }
    return index;
}

export const pieceOf = (pieces, key, option) => pieces?.[`${key}:${option}`] || null;
export const piecesOfCategory = (pieces, key) => Object.values(pieces || {}).filter((piece) => piece.item.startsWith(`${key}:`));
export const pieceName = (piece, locale) => piece?.name?.[locale === "en" ? "en" : "fr"] || piece?.item || "";

// L'avatar porte-t-il une pièce qui n'est pas dans le catalogue de base ? (Il faut alors charger les pièces.)
export function wearsCustomPiece(avatar) {
    if (!avatar || typeof avatar !== "object") return false;
    return CUSTOM_CATEGORIES.some((key) => {
        const value = avatar[key];
        return typeof value === "string" && !AVATAR_CATEGORIES.find((category) => category.key === key).options.includes(value);
    });
}
